import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { VideoAnalysisProvider } from './contexts/VideoAnalysisContext';
import { SkillAssessmentProvider } from './contexts/SkillAssessmentContext';

// Layout Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Navigation from './components/layout/Navigation';

// Screen Components
import HomeScreen from './screens/HomeScreen';
import VideoAnalysisScreen from './screens/VideoAnalysisScreen';
import SkillAssessmentScreen from './screens/SkillAssessmentScreen';
import DrillsScreen from './screens/DrillsScreen';
import StrategiesScreen from './screens/StrategiesScreen';
import EquipmentScreen from './screens/EquipmentScreen';
import FundamentalsScreen from './screens/FundamentalsScreen';
import ShotDiagnosisScreen from './screens/ShotDiagnosisScreen';
import GameAnalysisScreen from './screens/GameAnalysisScreen';
import RecoveryRehabScreen from './screens/RecoveryRehabScreen';
import PlayerMatchingScreen from './screens/PlayerMatchingScreen';
import TrailPrepScreen from './screens/TrailPrepScreen';
import SignUpScreen from './screens/SignUpScreen';
import SignInScreen from './screens/SignInScreen';
import ProfileScreen from './screens/ProfileScreen';
import BlogScreen from './screens/BlogScreen';
import WhitePaperScreen from './screens/WhitePaperScreen';
import ArticleDetailScreen from './screens/ArticleDetailScreen';
import CaseStudyDetailScreen from './screens/CaseStudyDetailScreen';
import NotFoundScreen from './screens/NotFoundScreen';
import PaymentSuccessScreen from './screens/PaymentSuccessScreen';
import PremiumGate from './components/PremiumGate';

// Styles
import './styles/global.css';
import './styles/components.css';
import './styles/content.css';

const App: React.FC = () => {
  // Get initial sport from environment or default to pickleball
  // For development, we'll default to pickleball since env files are not available
  const initialSport = 'pickleball';

  return (
    <ThemeProvider initialSport={initialSport}>
      <AuthProvider>
        <VideoAnalysisProvider>
          <SkillAssessmentProvider>
            <Router>
              <div className="app">
                <Header />
                <Navigation />
                
                <main className="main-content">
                  <Routes>
                    {/* Home Route */}
                    <Route path="/" element={<HomeScreen />} />
                    
                    {/* Core Features */}
                    <Route path="/game-analysis" element={<GameAnalysisScreen />} />
                    <Route path="/skill-assessment" element={
                      <PremiumGate
                        feature="Skill Assessment"
                        description="Test your knowledge, identify weak areas, and track your skill progress with personalized assessments."
                        benefits={[
                          { icon: '📊', text: 'Skill level assessments' },
                          { icon: '🎯', text: 'Weak area identification' },
                          { icon: '📈', text: 'Progress tracking over time' },
                          { icon: '✅', text: 'Personalized improvement paths' },
                        ]}
                      >
                        <SkillAssessmentScreen />
                      </PremiumGate>
                    } />
                    <Route path="/drills" element={<DrillsScreen />} />
                    <Route path="/strategies" element={<StrategiesScreen />} />
                    <Route path="/equipment" element={<EquipmentScreen />} />
                    <Route path="/fundamentals" element={<FundamentalsScreen />} />
                    <Route path="/recovery-rehab/:sport" element={<RecoveryRehabScreen />} />
                    <Route path="/recovery-rehab" element={<RecoveryRehabScreen />} />
                    <Route path="/coaching-marketplace" element={<Navigate to="/" replace />} />
                    <Route path="/find-players" element={<PlayerMatchingScreen />} />
                    <Route path="/trail-prep" element={<TrailPrepScreen />} />
                    
                    {/* Legacy routes for backward compatibility */}
                    <Route path="/video-analysis" element={<VideoAnalysisScreen />} />
                    <Route path="/shot-analysis" element={
                      <PremiumGate
                        feature="Shot Analysis"
                        description="Get personalized recommendations for improving your shots. Select shot types and issues for AI-enhanced coaching."
                        benefits={[
                          { icon: '🎯', text: 'Shot-by-shot diagnosis' },
                          { icon: '🤖', text: 'AI-powered recommendations' },
                          { icon: '🏥', text: 'Injury prevention & recovery links' },
                          { icon: '📋', text: 'Common issues by sport' },
                        ]}
                      >
                        <ShotDiagnosisScreen />
                      </PremiumGate>
                    } />
                    
                    {/* Blog */}
                    <Route path="/blog" element={<BlogScreen />} />
                    <Route path="/blog/whitepaper" element={<WhitePaperScreen />} />
                    <Route path="/blog/article/:id" element={<ArticleDetailScreen />} />
                    <Route path="/blog/case-study/:id" element={<CaseStudyDetailScreen />} />
                    
                    {/* Authentication */}
                    <Route path="/signup" element={<SignUpScreen />} />
                    <Route path="/signin" element={<SignInScreen />} />
                    <Route path="/profile" element={<ProfileScreen />} />
                    <Route path="/payment-success" element={<PaymentSuccessScreen />} />
                    
                    {/* Catch all route */}
                    <Route path="*" element={<NotFoundScreen />} />
                  </Routes>
                </main>
                
                <Footer />
              </div>
            </Router>
          </SkillAssessmentProvider>
        </VideoAnalysisProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
